"use client"
import { AnimatePresence, motion } from "motion/react"
import React, { CSSProperties } from "react"
import { useDimensions, useWindowSize } from "../../hooks"
import { classNames } from "../../utils"
import { useIsLessThanMd } from "../Media"
import { Portal } from "../Portal"
import { Text } from "../Text"

type BaseProps = {
  isHovered: boolean
  className?: string
}

type PropsWithAbsolutePositioning = BaseProps & {
  children: React.ReactNode
  positioning?: "bottom" | "top"
}

type PropsWithFixedPositioning = BaseProps & {
  containerRef: React.RefObject<HTMLElement | undefined | null>
  // This is useful when used for scrolling contexts
  stickyBottom?: CSSProperties["bottom"]
}

type Props = PropsWithAbsolutePositioning | PropsWithFixedPositioning

const PausedOnHoverBackground = ({
  className,
  children,
  style,
}: {
  className?: string
  children: React.ReactNode
  style?: React.CSSProperties
}) => (
  <motion.div
    animate={{ opacity: 1 }}
    className={classNames(
      "pointer-events-none z-[1] flex h-10 justify-center bg-frosted-2 px-2 py-3 shadow-sm backdrop-blur-2xl",
      className,
    )}
    exit={{ opacity: 0 }}
    initial={{ opacity: 0 }}
    style={style}
    transition={{ duration: 0.2, type: "spring", bounce: 0 }}
  >
    {children}
  </motion.div>
)

const PausedOnHoverText = () => {
  return (
    <Text.Label className="w-full text-center" size="xs">
      Paused on hover
    </Text.Label>
  )
}

const AbsolutePausedHoverable = ({
  isHovered,
  children,
  positioning = "bottom",
  className,
}: PropsWithAbsolutePositioning) => {
  return (
    <span className="relative">
      <AnimatePresence>
        {isHovered && (
          <PausedOnHoverBackground
            className={classNames(
              "absolute inset-x-0",
              positioning === "bottom" && "bottom-0",
              positioning === "top" && "top-0",
              className,
            )}
          >
            <PausedOnHoverText />
          </PausedOnHoverBackground>
        )}
      </AnimatePresence>

      {children}
    </span>
  )
}

const FixedPausedHoverable = ({
  isHovered,
  containerRef,
  stickyBottom,
  className,
}: PropsWithFixedPositioning) => {
  const rect = useDimensions(containerRef)
  const { width: windowWidth, height: windowHeight } = useWindowSize()
  const fixedPositionBottom = stickyBottom ?? (windowHeight ?? 0) - rect.bottom
  const fixedPositionLeft = rect.left
  const fixedPositionRight = (windowWidth ?? 0) - rect.right

  return (
    <Portal>
      <AnimatePresence>
        {isHovered ? (
          <PausedOnHoverBackground
            className={classNames("fixed", className)}
            style={{
              bottom: fixedPositionBottom,
              left: `${fixedPositionLeft}px`,
              right: `${fixedPositionRight}px`,
              width: `${rect.width}px`,
            }}
          >
            <PausedOnHoverText />
          </PausedOnHoverBackground>
        ) : null}
      </AnimatePresence>
    </Portal>
  )
}

export const PausedHoverable = ({ isHovered, ...rest }: Props) => {
  const isMobile = useIsLessThanMd()

  // For now, just ignore paused hover on mobile devices
  const hovered = isHovered && !isMobile

  if ("children" in rest) {
    return <AbsolutePausedHoverable isHovered={hovered} {...rest} />
  }

  return <FixedPausedHoverable isHovered={hovered} {...rest} />
}
